import React from 'react';
import { Button } from '@/components/ui/button';
import { Download, Eye, Link2, QrCode } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import CertificateService from '@/components/course/certificate/CertificateService';

interface CertificateQuickActionsProps {
  certificateId: string;
  certificateNumber: string;
  verificationHash?: string;
  onView?: () => void;
  onViewQR?: () => void;
  showQR?: boolean;
  variant?: 'card' | 'full';
}

export const CertificateQuickActions: React.FC<CertificateQuickActionsProps> = ({
  certificateId,
  certificateNumber,
  verificationHash,
  onView,
  onViewQR,
  showQR = true,
  variant = 'card'
}) => {
  const handleDownload = async () => {
    try {
      const elementId = `certificate-${certificateId}`;
      const filename = `Certificate_${certificateNumber}`;
      await CertificateService.generatePDF(elementId, filename);
      toast.success('Certificate ready for download!');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate. Please try viewing first.');
    }
  };

  const handleCopyLink = () => {
    const baseUrl = window.location.origin;
    const verificationUrl = verificationHash
      ? `${baseUrl}/certificate/verify/${verificationHash}`
      : `${baseUrl}/certificates/${certificateId}`;

    navigator.clipboard.writeText(verificationUrl);
    toast.success('Verification link copied to clipboard!', {
      description: 'Share this link to verify your certificate'
    });
  };

  if (variant === 'card') {
    return (
      <div className="flex gap-2">
        {onView && (
          <Button onClick={onView} size="sm" className="flex-1">
            <Eye className="h-3 w-3 mr-1" />
            View
          </Button>
        )}
        <Button onClick={handleDownload} size="sm" variant="outline" className="flex-1">
          <Download className="h-3 w-3 mr-1" />
          Download
        </Button>
        <Button onClick={handleCopyLink} size="sm" variant="outline">
          <Link2 className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {onView && (
        <Button onClick={onView} size="sm">
          <Eye className="h-4 w-4 mr-2" />
          View Full Certificate
        </Button>
      )}
      <Button onClick={handleDownload} size="sm" variant="outline">
        <Download className="h-4 w-4 mr-2" />
        Download PDF
      </Button>
      <Button onClick={handleCopyLink} size="sm" variant="outline">
        <Link2 className="h-4 w-4 mr-2" />
        Copy Verification Link
      </Button>
      {showQR && onViewQR && (
        <Button onClick={onViewQR} size="sm" variant="outline">
          <QrCode className="h-4 w-4 mr-2" />
          View QR Code
        </Button>
      )}
    </div>
  );
};
